import { useRef, useState } from "react";
import { toast } from "sonner";
import { Images, Trash2 } from "lucide-react";
import { api, imgSrc, errMsg, prepareImage } from "../../lib/api";

export default function StudioLibrary({ library, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);

  const uploadFiles = async (files) => {
    const list = [...(files || [])].filter((f) => f.type.startsWith("image/"));
    if (!list.length) {
      toast.error("Choose image files");
      return;
    }
    if (list.some((f) => f.size > 25 * 1024 * 1024)) {
      toast.error("One of the images is larger than 25 MB");
      return;
    }
    setBusy(true);
    try {
      for (const f of list) {
        const fd = new FormData();
        fd.append("file", await prepareImage(f));
        await api.post("/library", fd);
      }
      toast.success(
        list.length === 1
          ? "Photo added to your library"
          : `${list.length} photos added to your library`
      );
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (p) => {
    try {
      await api.delete(`/library/${p.id}`);
      toast.success("Photo removed from the library");
      onChanged();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <section data-testid="library-section" className="border border-line bg-panel p-6 md:p-8">
      <div className="flex items-center gap-3">
        <Images size={14} className="text-gold" />
        <p className="eyebrow !text-ochre/90">Storage</p>
      </div>
      <div className="flex items-end justify-between gap-4 flex-wrap mt-2">
        <h2 className="font-display text-2xl">Photo Library</h2>
        <p className="font-meta text-[10px] uppercase tracking-[0.25em] text-mute" data-testid="library-count">
          {String(library.length).padStart(2, "0")} photos stored
        </p>
      </div>

      <div
        data-testid="library-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          uploadFiles(e.dataTransfer.files);
        }}
        onClick={() => inputRef.current?.click()}
        className={`mt-6 border border-dashed p-6 text-center cursor-pointer transition-colors duration-300 ${
          drag ? "border-gold bg-gold/5" : "border-white/20 hover:border-gold/60"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          data-testid="library-upload-input"
          onChange={(e) => {
            uploadFiles(e.target.files);
            e.target.value = "";
          }}
        />
        <p className="font-display italic text-lg">
          {busy ? "Storing your photos…" : "Drop photos here or click to upload — as many as you like"}
        </p>
        <p className="font-meta text-[9px] uppercase tracking-[0.3em] text-mute mt-2">
          They stay here, ready to pick for any artwork
        </p>
      </div>

      {library.length === 0 ? (
        <p className="font-meta text-[9px] uppercase tracking-[0.25em] text-mute mt-4">
          Your library is empty — upload photos once, then choose them for every new piece without opening your camera roll.
        </p>
      ) : (
        <div className="mt-6 grid grid-cols-3 md:grid-cols-6 gap-3">
          {library.map((p) => (
            <div key={p.id} className="relative group border border-line" data-testid={`library-photo-${p.id}`}>
              <img
                src={imgSrc(p.storage_path)}
                alt={p.original_filename || "Library photo"}
                loading="lazy"
                decoding="async"
                className="w-full aspect-square object-cover"
              />
              <button
                onClick={() => remove(p)}
                data-testid={`library-delete-button-${p.id}`}
                aria-label="Remove photo from library"
                className="absolute top-1.5 right-1.5 bg-ink/80 backdrop-blur p-1.5 text-mute hover:text-red-400 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
